import {
  NodeMaterialDto,
  NodeSupplementalDto,
} from '@api/apiClient';
import { NodeDto } from '@memorypack/NodeDto';

/**
 * Generates a composite key for nodeMaterials lookup.
 * Key format: `${scenarioId}:${nodeId}`
 * @param scenarioId Scenario ID
 * @param nodeId Node ID
 * @returns Composite key string
 */
export function createNodeMaterialKey(scenarioId: string, nodeId: bigint | number): string {
  return `${scenarioId}:${nodeId}`;
}

/**
 * Centralized data service for managing model data including nodes, materials, and supplementals.
 * Provides a single source of truth for all model-related data used by LCAoMService,
 * TreeBuilder, and React components.
 * 
 * Data ownership:
 * - nodes: Static data from OPFS, keyed by modelId
 * - nodeSupplementals: Dynamic data from server, keyed by nodeId
 * - nodeMaterials: Dynamic data managed via LCAoMService mutations, keyed by scenarioId:nodeId
 */
export class ModelsDataService {
  // Node data (static, from OPFS) - keyed by modelId
  private nodesByModelId: Map<string, NodeDto[]> = new Map();

  // NodeSupplementals lookup (dynamic, from server) - keyed by nodeId
  private supplementalsByNodeId: Map<number, NodeSupplementalDto> = new Map();

  // NodeMaterials lookup (dynamic, managed by LCAoMService mutations) - keyed by scenarioId:nodeId
  private materialsByKey: Map<string, NodeMaterialDto> = new Map();

  // Original nodeMaterials for change tracking
  private originalMaterialsByKey: Map<string, NodeMaterialDto> = new Map();

  // Version counter for React reactivity
  private versionCounter: number = 0;

  // ============================================================================
  // Version / Reactivity
  // ============================================================================

  /**
   * Version counter that increments on every data mutation.
   * Use this as a React dependency to trigger re-renders when data changes.
   */
  get version(): number {
    return this.versionCounter;
  }

  /**
   * Increments the version counter to signal a data change.
   * Should be called after any mutation to the data maps.
   */
  incrementVersion(): void {
    this.versionCounter++;
  }

  // ============================================================================
  // Node Data Methods (static, from OPFS)
  // ============================================================================

  /**
   * Sets nodes for a specific model.
   * @param modelId The model ID
   * @param nodes Array of NodeDto for the model
   */
  setNodes(modelId: string, nodes: NodeDto[]): void {
    this.nodesByModelId.set(modelId, nodes);
    this.incrementVersion();
  }

  /**
   * Gets nodes for a specific model.
   * @param modelId The model ID
   * @returns Array of NodeDto or undefined if not found
   */
  getNodes(modelId: string): NodeDto[] | undefined {
    return this.nodesByModelId.get(modelId);
  }

  /**
   * Gets all nodes from all models combined.
   * @returns Flattened array of all NodeDto
   */
  getAllNodes(): NodeDto[] {
    const allNodes: NodeDto[] = [];
    for (const nodes of this.nodesByModelId.values()) {
      allNodes.push(...nodes);
    }
    return allNodes;
  }

  /**
   * Gets the internal nodes map (read-only access for iteration).
   */
  get nodes(): ReadonlyMap<string, NodeDto[]> {
    return this.nodesByModelId;
  }

  /**
   * Checks if nodes are loaded for a model.
   * @param modelId The model ID
   */
  hasNodes(modelId: string): boolean {
    return this.nodesByModelId.has(modelId);
  }

  /**
   * Clears nodes for a specific model.
   * @param modelId The model ID
   */
  clearNodes(modelId: string): void {
    this.nodesByModelId.delete(modelId);
    this.incrementVersion();
  }

  /**
   * Clears all nodes.
   */
  clearAllNodes(): void {
    this.nodesByModelId.clear();
    this.incrementVersion();
  }

  // ============================================================================
  // NodeSupplementals Methods (dynamic, from server)
  // ============================================================================

  /**
   * Sets the nodeSupplementals lookup map.
   * @param nodeSupplementals Map of nodeId to NodeSupplementalDto
   */
  setNodeSupplementals(nodeSupplementals: Map<number, NodeSupplementalDto>): void {
    this.supplementalsByNodeId = new Map(nodeSupplementals);
    this.incrementVersion();
  }

  /**
   * Gets a nodeSupplemental by nodeId.
   * @param nodeId The node ID
   * @returns NodeSupplementalDto or undefined if not found
   */
  getNodeSupplemental(nodeId: number): NodeSupplementalDto | undefined {
    return this.supplementalsByNodeId.get(nodeId);
  }

  /**
   * Gets the nodeSupplementals lookup map.
   * Returns the internal map directly for performance in grid column lookups.
   */
  get nodeSupplementals(): Map<number, NodeSupplementalDto> {
    return this.supplementalsByNodeId;
  }

  /**
   * Clears all nodeSupplementals.
   */
  clearNodeSupplementals(): void {
    this.supplementalsByNodeId.clear();
    this.incrementVersion();
  }

  // ============================================================================
  // NodeMaterials Methods (dynamic, managed by LCAoMService)
  // ============================================================================

  /**
   * Sets a nodeMaterial in the lookup.
   * @param key Composite key in format `${scenarioId}:${nodeId}`
   * @param nodeMaterial The NodeMaterialDto to store
   */
  setNodeMaterial(key: string, nodeMaterial: NodeMaterialDto): void {
    this.materialsByKey.set(key, nodeMaterial);
    // Note: incrementVersion is NOT called here - caller should batch updates
    // and call incrementVersion once after all mutations
  }

  /**
   * Gets a nodeMaterial by key.
   * @param key Composite key in format `${scenarioId}:${nodeId}`
   * @returns NodeMaterialDto or undefined if not found
   */
  getNodeMaterial(key: string): NodeMaterialDto | undefined {
    return this.materialsByKey.get(key);
  }

  /**
   * Deletes a nodeMaterial by key.
   * @param key Composite key in format `${scenarioId}:${nodeId}`
   * @returns true if the material was deleted, false if it didn't exist
   */
  deleteNodeMaterial(key: string): boolean {
    return this.materialsByKey.delete(key);
    // Note: incrementVersion is NOT called here - caller should batch updates
  }

  /**
   * Gets the nodeMaterials lookup map.
   * Returns the internal map directly for performance in grid column lookups.
   * Key format: `${scenarioId}:${nodeId}`
   */
  get nodeMaterials(): Map<string, NodeMaterialDto> {
    return this.materialsByKey;
  }

  /**
   * Gets the count of nodeMaterials.
   */
  get nodeMaterialsCount(): number {
    return this.materialsByKey.size;
  }

  /**
   * Gets the count of nodeMaterials for a specific scenario.
   * @param scenarioId The scenario ID
   */
  getNodeMaterialCount(scenarioId: string): number {
    const keyPrefix = `${scenarioId}:`;
    const prefixLength = keyPrefix.length;
    let count = 0;

    for (const key of this.materialsByKey.keys()) {
      if (key.length >= prefixLength && key.substring(0, prefixLength) === keyPrefix) {
        count++;
      }
    }
    return count;
  }

  /**
   * Clears all nodeMaterials.
   */
  clearNodeMaterials(): void {
    this.materialsByKey.clear();
    this.incrementVersion();
  }

  // ============================================================================
  // Original NodeMaterials / Change Tracking
  // ============================================================================

  /**
   * Loads nodeMaterials from server data and sets as original baseline.
   * Clears existing materials and populates both current and original maps.
   * @param nodeMaterialsMap Map of nodeId to NodeMaterialDto (from server)
   * @param scenarioId Scenario ID for composite key generation
   */
  loadOriginalNodeMaterials(nodeMaterialsMap: Map<number, NodeMaterialDto>, scenarioId: string): void {
    console.log(`[ModelsDataService:loadOriginalNodeMaterials] Loading ${nodeMaterialsMap.size} materials for scenario ${scenarioId}`);
    console.log(`[ModelsDataService:loadOriginalNodeMaterials] BEFORE CLEAR - nodeMaterials.size: ${this.materialsByKey.size}, originalNodeMaterials.size: ${this.originalMaterialsByKey.size}`);

    this.materialsByKey.clear();
    this.originalMaterialsByKey.clear();

    for (const [nodeId, material] of nodeMaterialsMap) {
      if (!material.modelId) {
        continue;
      }
      const key = createNodeMaterialKey(scenarioId, nodeId);
      this.materialsByKey.set(key, material);
      this.originalMaterialsByKey.set(key, new NodeMaterialDto({ ...material }));
    }

    console.log(`[ModelsDataService:loadOriginalNodeMaterials] AFTER LOAD - nodeMaterials.size: ${this.materialsByKey.size}`);
    this.incrementVersion();
  }

  /**
   * Gets an original nodeMaterial by key (before any local modifications).
   * @param key Composite key in format `${scenarioId}:${nodeId}`
   * @returns Original NodeMaterialDto or undefined if not found
   */
  getOriginalNodeMaterial(key: string): NodeMaterialDto | undefined {
    return this.originalMaterialsByKey.get(key);
  }

  /**
   * Gets the original nodeMaterials map (read-only access).
   */
  get originalNodeMaterials(): ReadonlyMap<string, NodeMaterialDto> {
    return this.originalMaterialsByKey;
  }

  /**
   * Checks if there are any local changes to nodeMaterials for a scenario.
   * @param scenarioId The scenario ID to check
   * @returns true if there are unsaved changes
   */
  hasChanges(scenarioId: string): boolean {
    const keyPrefix = `${scenarioId}:`;
    const prefixLength = keyPrefix.length;

    // Check for added or updated materials
    for (const [key, current] of this.materialsByKey) {
      if (key.length >= prefixLength && key.substring(0, prefixLength) === keyPrefix) {
        const original = this.originalMaterialsByKey.get(key);
        if (!original || this.hasMaterialChanged(original, current)) {
          return true;
        }
      }
    }

    // Check for removed materials
    for (const key of this.originalMaterialsByKey.keys()) {
      if (key.length >= prefixLength && key.substring(0, prefixLength) === keyPrefix) {
        if (!this.materialsByKey.has(key)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Checks if there are any local changes across all scenarios.
   * @returns true if there are any unsaved changes
   */
  hasLocalChanges(): boolean {
    // Quick check: different sizes means changes
    if (this.materialsByKey.size !== this.originalMaterialsByKey.size) {
      return true;
    }

    // Check for any modifications
    for (const [key, current] of this.materialsByKey) {
      const original = this.originalMaterialsByKey.get(key);
      if (!original || this.hasMaterialChanged(original, current)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Compares two materials to check if they differ.
   * @param original The original material
   * @param current The current material
   * @returns true if the materials differ
   */
  private hasMaterialChanged(original: NodeMaterialDto, current: NodeMaterialDto): boolean {
    return (
      original.co2MaterialTypeId !== current.co2MaterialTypeId ||
      original.a1A2A3 !== current.a1A2A3 ||
      original.a4 !== current.a4 ||
      original.a5 !== current.a5 ||
      original.b1 !== current.b1 ||
      original.b2 !== current.b2 ||
      original.b3 !== current.b3 ||
      original.b4 !== current.b4 ||
      original.b5 !== current.b5 ||
      original.b6 !== current.b6 ||
      original.b7 !== current.b7 ||
      original.c1 !== current.c1 ||
      original.c2 !== current.c2 ||
      original.c3 !== current.c3 ||
      original.c4 !== current.c4 ||
      original.d !== current.d ||
      original.biogenic !== current.biogenic
    );
  }

  /**
   * Deep clones the nodeMaterials map.
   * Used for undo/redo memento creation.
   * @returns A new Map with cloned NodeMaterialDto instances
   */
  cloneNodeMaterials(): Map<string, NodeMaterialDto> {
    const clone = new Map<string, NodeMaterialDto>();
    for (const [key, material] of this.materialsByKey) {
      clone.set(key, new NodeMaterialDto({ ...material }));
    }
    return clone;
  }

  /**
   * Restores nodeMaterials from a cloned map.
   * Used for undo/redo memento restoration.
   * @param materials Map of materials to restore
   */
  restoreNodeMaterials(materials: Map<string, NodeMaterialDto>): void {
    this.materialsByKey = new Map(materials);
    this.incrementVersion();
  }

  // ============================================================================
  // Lifecycle Methods
  // ============================================================================

  /**
   * Clears all data from the service.
   */
  clear(): void {
    this.nodesByModelId.clear();
    this.supplementalsByNodeId.clear();
    this.materialsByKey.clear();
    this.originalMaterialsByKey.clear();
    this.incrementVersion();
  }

  /**
   * Clears only dynamic data (materials and supplementals), keeping static nodes.
   */
  clearDynamicData(): void {
    this.supplementalsByNodeId.clear();
    this.materialsByKey.clear();
    this.originalMaterialsByKey.clear();
    this.incrementVersion();
  }
}
